import { LocationSearchHit, Place } from "@/types/place"
import isEqual from "lodash/isEqual"
import uniq from "lodash/uniq"

import { exists } from "@/lib/utils/presence"

export function fullLocationName(
  location: { name: string; city?: string; region?: string; country?: string },
  includeRegion?: IncludeName,
  includeCountry?: IncludeName
): string {
  includeRegion = includeRegion ?? DefaultIncludeRegion
  includeCountry = includeCountry ?? DefaultIncludeCountry

  const parts = []
  parts.push(location.name)
  parts.push(location.city)
  if (shouldInclude(includeRegion, location.country)) {
    parts.push(location.region)
  }
  if (shouldInclude(includeCountry, location.country)) {
    parts.push(location.country)
  }

  return uniq(parts).filter(exists).join(", ")
}

// Given an item like a geo (where ancestors are like those
// generated by the server method Geo#plain_ancestor_names),
// and a list of peers that the location is going to be among,
// returns a string that will describe the location. Using this
// on ever item in the list will produce a list with no duplicates.
//
// It's ok if the "others" includes the location being formatted.
export function unambiguousLocationName(
  location: {
    name: string
    city?: string
    region?: string
    country?: string
    ancestors?: string[]
  },
  others: Array<{
    name: string
    city?: string
    region?: string
    country?: string
  }>,
  includeRegion?: IncludeName,
  includeCountry?: IncludeName
) {
  const otherNames = others
    .filter((o) => !isEqual(o, location))
    .map((o) => fullLocationName(o, includeRegion, includeCountry))
  const test1 = fullLocationName(location, includeRegion, includeCountry)
  if (!otherNames.includes(test1)) {
    return test1
  }

  const test2 = fullLocationName(location, true, includeCountry)
  if (!otherNames.includes(test2)) {
    return test2
  }

  const test3 = fullLocationName(location, includeRegion, true)
  if (!otherNames.includes(test3)) {
    return test3
  }

  const test4 = fullLocationName(location, true, true)
  if (!otherNames.includes(test4)) {
    return test4
  }

  const parts = [test4]
  if (location.ancestors) {
    parts.push(...location.ancestors)
  }
  return uniq(parts).join(", ")
}

export function locationAncestorString(location: {
  name: string
  city?: string
  region?: string
  country?: string
  category?: string
}): string {
  let parts = []

  parts.push(location.city)
  parts.push(location.region)
  parts.push(location.country)
  parts = uniq(parts).filter(exists)
  if (location.name === parts[0]) {
    parts.shift()
  }
  if (parts.length === 0) {
    return location.category || ""
  } else {
    return parts.join(", ")
  }
}

type IncludeName = boolean | "if-us" | "if-us-or-ca" | "unless-us"

function shouldInclude(include: IncludeName, country: string | undefined) {
  return (
    include === true ||
    (include === "if-us" && country === "United States") ||
    (include === "if-us-or-ca" &&
      (country === "United States" || country === "Canada")) ||
    (include === "unless-us" && country !== "United States")
  )
}

const DefaultIncludeRegion: IncludeName = "if-us-or-ca"
const DefaultIncludeCountry: IncludeName = "unless-us"

export function isLocationSearchHit(
  place: Place | LocationSearchHit
): place is LocationSearchHit {
  return "ancestors" in place
}
